import React, { useEffect, useState, createElement, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { questions, profiles, blockAnalysis, WEBHOOK_URL, WHATSAPP_NUMBER, ProfileType, BlockType } from './data';
type Screen = 'entry' | 'questions' | 'capture' | 'results';
export function DiagnosticoPage() {
  const [currentScreen, setCurrentScreen] = useState<Screen>('entry');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<number[]>(new Array(12).fill(0));
  const [formData, setFormData] = useState({
    nombre: '',
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  // Persist state
  useEffect(() => {
    const savedState = sessionStorage.getItem('diagnosticoState');
    if (savedState) {
      const parsed = JSON.parse(savedState);
      if (parsed.answers) setAnswers(parsed.answers);
      if (parsed.currentScreen) setCurrentScreen(parsed.currentScreen);
      if (parsed.currentQuestionIndex) setCurrentQuestionIndex(parsed.currentQuestionIndex);
    }
  }, []);
  useEffect(() => {
    sessionStorage.setItem('diagnosticoState', JSON.stringify({
      answers,
      currentScreen,
      currentQuestionIndex
    }));
  }, [answers, currentScreen, currentQuestionIndex]);
  // Meta tags for standalone page
  useEffect(() => {
    document.title = 'Diagnóstico Estratégico — ¿Tu negocio llegó a su techo?';
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (!metaRobots) {
      metaRobots = document.createElement('meta');
      metaRobots.setAttribute('name', 'robots');
      document.head.appendChild(metaRobots);
    }
    metaRobots.setAttribute('content', 'noindex, nofollow');
  }, []);
  // Redirect if accessing results without completing
  useEffect(() => {
    if (currentScreen === 'results' && answers.includes(0)) {
      setCurrentScreen('entry');
    }
  }, [currentScreen, answers]);
  const handleAnswer = (value: number) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestionIndex] = value;
    setAnswers(newAnswers);
  };
  const handleNext = () => {
    if (currentQuestionIndex < 11) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      setCurrentScreen('capture');
    }
  };
  const handlePrev = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };
  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!formData.nombre.trim()) {
      setError('Por favor ingresa tu nombre.');
      return;
    }
    if (!validateEmail(formData.email)) {
      setError('Por favor ingresa un email válido.');
      return;
    }
    setIsSubmitting(true);
    const puntaje_estrategia = answers.slice(0, 4).reduce((a, b) => a + b, 0);
    const puntaje_estructura = answers.slice(4, 8).reduce((a, b) => a + b, 0);
    const puntaje_tecnologia = answers.slice(8, 12).reduce((a, b) => a + b, 0);
    const puntaje_total = puntaje_estrategia + puntaje_estructura + puntaje_tecnologia;
    let perfil: ProfileType = 'techo';
    if (puntaje_total >= 25 && puntaje_total <= 36) perfil = 'cerca';
    if (puntaje_total >= 37) perfil = 'paso';
    const scores = [{
      name: 'estrategia',
      score: puntaje_estrategia
    }, {
      name: 'estructura',
      score: puntaje_estructura
    }, {
      name: 'tecnologia',
      score: puntaje_tecnologia
    }];
    const minScore = Math.min(...scores.map(s => s.score));
    const weakestBlocks = scores.filter(s => s.score === minScore);
    const bloque_debil = weakestBlocks.length > 1 ? 'empate' : weakestBlocks[0].name;
    const payload = {
      nombre: formData.nombre,
      email: formData.email,
      puntaje_total,
      puntaje_estrategia,
      puntaje_estructura,
      puntaje_tecnologia,
      perfil,
      bloque_debil,
      respuestas: answers,
      timestamp: new Date().toISOString()
    };
    if (WEBHOOK_URL) {
      try {
        await fetch(WEBHOOK_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
      } catch (err) {
        console.error('Webhook failed', err);
        // Continue to results even if webhook fails
      }
    }
    setIsSubmitting(false);
    setCurrentScreen('results');
  };
  const renderEntry = () => <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="min-h-screen flex flex-col items-center justify-center px-6 text-center" data-id="element-152">
      <img src="https://cdn.magicpatterns.com/uploads/8iq9FdZPWhhg1moFGMfStg/Gemini_Generated_Image_vohteevohteevoht_(1)-Photoroom.png" alt="OR Logo" className="h-16 mb-12" data-id="element-153" />
      <h1 className="text-4xl md:text-6xl font-serif font-bold text-white mb-4" data-id="element-154">
        ¿Tu negocio llegó a su techo?
      </h1>
      <h2 className="text-2xl md:text-3xl font-serif text-gold mb-8" data-id="element-155">
        Descúbrelo en 5 minutos.
      </h2>
      <p className="text-gray-light text-lg max-w-[600px] mb-12 leading-relaxed" data-id="element-156">
        12 preguntas diseñadas para identificar dónde está el punto de fricción
        real de tu negocio — y qué necesitas para llegar al siguiente nivel. No
        es un test genérico. Es el mismo diagnóstico que aplico con dueños de
        negocio que ya tienen operación activa y saben que pueden ir más lejos.
      </p>
      <button onClick={() => setCurrentScreen('questions')} className="bg-gold hover:bg-yellow-500 text-navy font-bold text-xl px-10 py-5 rounded transition-all duration-300 hover:scale-105 shadow-[0_0_20px_rgba(201,168,76,0.3)]" data-id="element-157">
        Comenzar diagnóstico →
      </button>
      <p className="text-sm text-gray-light/60 mt-4" data-id="element-158">
        Para dueños de negocio con operación activa. Toma 5 minutos.
      </p>
    </motion.div>;
  const renderQuestion = () => {
    const q = questions[currentQuestionIndex];
    const progress = (currentQuestionIndex + 1) / 12 * 100;
    const hasAnswered = answers[currentQuestionIndex] !== 0;
    return <motion.div key={currentQuestionIndex} initial={{
      opacity: 0,
      x: 20
    }} animate={{
      opacity: 1,
      x: 0
    }} exit={{
      opacity: 0,
      x: -20
    }} className="min-h-screen flex flex-col px-6 py-12 max-w-3xl mx-auto w-full" data-id="element-159">
        <div className="w-full mb-12" data-id="element-160">
          <p className="text-xs text-gray-light mb-2 text-right" data-id="element-161">
            Pregunta {currentQuestionIndex + 1} de 12
          </p>
          <div className="w-full h-1 bg-navy-dark rounded-full overflow-hidden" data-id="element-162">
            <div className="h-full bg-gold transition-all duration-500" style={{
            width: `${progress}%`
          }} data-id="element-163" />
          </div>
        </div>

        <div className="flex-grow flex flex-col justify-center" data-id="element-164">
          <p className="text-gold text-sm font-bold tracking-wider mb-6 text-center" data-id="element-165">
            {q.blockName}
          </p>
          <h2 className="text-2xl md:text-4xl font-serif text-white text-center mb-12 leading-tight" data-id="element-166">
            {q.text}
          </h2>

          <div className="space-y-4 w-full max-w-[600px] mx-auto" data-id="element-167">
            {q.options.map((opt, i) => {
            const isSelected = answers[currentQuestionIndex] === opt.value;
            return <button key={i} onClick={() => handleAnswer(opt.value)} className={`w-full text-left p-6 rounded border transition-all duration-200 ${isSelected ? 'bg-gold border-gold text-navy font-medium' : 'bg-navy border-gold/30 text-white hover:bg-[#252540] hover:border-gold/60'}`} data-id="element-168">
                  {opt.text}
                </button>;
          })}
          </div>
        </div>

        <div className="flex justify-between items-center mt-12 pt-6 border-t border-white/10" data-id="element-169">
          {currentQuestionIndex > 0 ? <button onClick={handlePrev} className="text-gray-light hover:text-white transition-colors px-4 py-2" data-id="element-170">
              ← Anterior
            </button> : <div data-id="element-171"></div>}

          <button onClick={handleNext} disabled={!hasAnswered} className={`font-bold px-8 py-3 rounded transition-all ${hasAnswered ? 'bg-gold text-navy hover:bg-yellow-500' : 'bg-navy-dark text-gray-light/30 cursor-not-allowed border border-white/10'}`} data-id="element-172">
            Siguiente →
          </button>
        </div>
      </motion.div>;
  };
  const renderCapture = () => <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="min-h-screen flex flex-col items-center justify-center px-6 text-center" data-id="element-173">
      <h2 className="text-3xl md:text-5xl font-serif text-white mb-4" data-id="element-174">
        Estás a un paso de ver tu resultado.
      </h2>
      <p className="text-gray-light text-lg mb-12" data-id="element-175">
        Te enviamos también un resumen a tu correo para que lo revises con
        calma.
      </p>

      <form onSubmit={handleSubmit} className="w-full max-w-[500px] space-y-6" data-id="element-176">
        <div data-id="element-177">
          <input type="text" placeholder="Tu nombre" value={formData.nombre} onChange={e => setFormData({
          ...formData,
          nombre: e.target.value
        })} className="w-full bg-navy-dark border border-gold/30 rounded p-4 text-white focus:outline-none focus:border-gold transition-colors" data-id="element-178" />
        </div>
        <div data-id="element-179">
          <input type="email" placeholder="Tu email" value={formData.email} onChange={e => setFormData({
          ...formData,
          email: e.target.value
        })} className="w-full bg-navy-dark border border-gold/30 rounded p-4 text-white focus:outline-none focus:border-gold transition-colors" data-id="element-180" />
        </div>

        {error && <p className="text-red-400 text-sm text-left" data-id="element-181">{error}</p>}

        <button type="submit" disabled={isSubmitting} className="w-full bg-gold hover:bg-yellow-500 text-navy font-bold text-xl px-8 py-4 rounded transition-all duration-300 disabled:opacity-70" data-id="element-182">
          {isSubmitting ? 'Procesando...' : 'Ver mi diagnóstico →'}
        </button>
        <p className="text-sm text-gray-light/60" data-id="element-183">
          Respeto tu tiempo y tu bandeja de entrada. Nada de spam.
        </p>
      </form>
    </motion.div>;
  const renderResults = () => {
    const puntaje_estrategia = answers.slice(0, 4).reduce((a, b) => a + b, 0);
    const puntaje_estructura = answers.slice(4, 8).reduce((a, b) => a + b, 0);
    const puntaje_tecnologia = answers.slice(8, 12).reduce((a, b) => a + b, 0);
    const puntaje_total = puntaje_estrategia + puntaje_estructura + puntaje_tecnologia;
    let perfil: ProfileType = 'techo';
    if (puntaje_total >= 25 && puntaje_total <= 36) perfil = 'cerca';
    if (puntaje_total >= 37) perfil = 'paso';
    const profileData = profiles[perfil];
    const scores = [{
      name: 'estrategia',
      score: puntaje_estrategia,
      label: 'ESTRATEGIA'
    }, {
      name: 'estructura',
      score: puntaje_estructura,
      label: 'ESTRUCTURA'
    }, {
      name: 'tecnologia',
      score: puntaje_tecnologia,
      label: 'TECNOLOGÍA'
    }];
    const minScore = Math.min(...scores.map(s => s.score));
    const weakestBlocks = scores.filter(s => s.score === minScore);
    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(profileData.whatsappMsg)}`;
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="min-h-screen py-20 px-6 max-w-3xl mx-auto" data-id="element-184">
        {/* Section A */}
        <div className="text-center mb-16" data-id="element-185">
          <div className="inline-block border border-gold/30 rounded-full px-6 py-2 mb-8" data-id="element-186">
            <span className="text-4xl font-bold text-white" data-id="element-187">
              {puntaje_total}
            </span>
            <span className="text-xl text-gray-light" data-id="element-188"> / 48</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-serif text-gold mb-4 leading-tight" data-id="element-189">
            {profileData.headline}
          </h1>
          <p className="text-gray-light uppercase tracking-widest text-sm" data-id="element-190">
            Tu resultado
          </p>
        </div>

        {/* Section B */}
        <div className="space-y-6 text-lg text-white/90 leading-relaxed mb-20" data-id="element-191">
          {profileData.analysis.map((p, i) => <p key={i} data-id="element-192">{p}</p>)}
        </div>

        {/* Section C */}
        <div className="mb-20 bg-navy-dark p-8 rounded-xl border border-white/5" data-id="element-193">
          <h3 className="text-xl font-serif text-white mb-8 text-center" data-id="element-194">
            Desglose por áreas
          </h3>
          <div className="space-y-8" data-id="element-195">
            {scores.map((s, i) => <div key={i} data-id="element-196">
                <div className="flex justify-between text-sm mb-2" data-id="element-197">
                  <span className="text-gold font-bold tracking-wider" data-id="element-198">
                    {s.label}
                  </span>
                  <span className="text-gray-light" data-id="element-199">{s.score} / 16</span>
                </div>
                <div className="w-full h-2 bg-navy rounded-full overflow-hidden" data-id="element-200">
                  <div className="h-full bg-gold" style={{
                width: `${s.score / 16 * 100}%`
              }} data-id="element-201" />
                </div>
              </div>)}
          </div>
        </div>

        {/* Section D */}
        <div className="mb-20" data-id="element-202">
          {weakestBlocks.length > 1 ? <>
              <h2 className="text-2xl font-serif text-gold mb-8" data-id="element-203">
                Tus puntos más débiles están en{' '}
                {weakestBlocks.map(b => b.label).join(' y ')}. Ambos requieren
                atención.
              </h2>
              {weakestBlocks.map((b, i) => <div key={i} className="mb-12" data-id="element-204">
                  <h3 className="text-xl font-bold text-white mb-4" data-id="element-205">
                    {b.label}
                  </h3>
                  <div className="space-y-4 text-gray-light leading-relaxed" data-id="element-206">
                    {blockAnalysis[b.name as BlockType].map((p, j) => <p key={j} data-id="element-207">{p}</p>)}
                  </div>
                </div>)}
            </> : <>
              <h2 className="text-2xl font-serif text-gold mb-8" data-id="element-208">
                Tu punto más débil: {weakestBlocks[0].label}
              </h2>
              <div className="space-y-4 text-gray-light leading-relaxed" data-id="element-209">
                {blockAnalysis[weakestBlocks[0].name as BlockType].map((p, i) => <p key={i} data-id="element-210">{p}</p>)}
              </div>
            </>}
        </div>

        {/* Section E */}
        <div className="text-center bg-gold/10 border border-gold/30 p-8 md:p-12 rounded-xl mb-20" data-id="element-211">
          <p className="text-xl text-white mb-8 leading-relaxed" data-id="element-212">
            {profileData.nextStep}
          </p>
          <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-block bg-gold hover:bg-yellow-500 text-navy font-bold text-xl px-10 py-5 rounded transition-all duration-300 hover:scale-105 shadow-xl" data-id="element-213">
            Hablemos por WhatsApp →
          </a>
        </div>

        {/* Section F */}
        <div className="text-center border-t border-white/10 pt-12 pb-8" data-id="element-214">
          <img src="https://cdn.magicpatterns.com/uploads/8iq9FdZPWhhg1moFGMfStg/Gemini_Generated_Image_vohteevohteevoht_(1)-Photoroom.png" alt="OR Logo" className="h-10 mx-auto mb-4 opacity-50" data-id="element-215" />
          <p className="text-sm text-gray-light mb-2" data-id="element-216">
            Oscar Rodriguez — Arquitecto de Negocios
          </p>
          <a href="https://instagram.com/oscarrodriguezm" target="_blank" rel="noopener noreferrer" className="text-gold hover:text-yellow-500 text-sm" data-id="element-217">
            @oscarrodriguezm
          </a>
        </div>
      </motion.div>;
  };
  return <div className="min-h-screen bg-navy text-white font-sans selection:bg-gold/30 selection:text-white" data-id="element-218">
      <AnimatePresence mode="wait" data-id="element-219">
        {currentScreen === 'entry' && renderEntry()}
        {currentScreen === 'questions' && renderQuestion()}
        {currentScreen === 'capture' && renderCapture()}
        {currentScreen === 'results' && renderResults()}
      </AnimatePresence>
    </div>;
}